import React from "react";
import { COLORS } from "../mockData";
import PatientScoreTrendChart from "./PatientScoreTrendChart";

const TAG_COLORS = {
  danger: { bg: "#FEE2E2", color: "#DC2626", border: "#FECACA" },
  warning: { bg: "#FEF3C7", color: "#D97706", border: "#FDE68A" },
  info: { bg: "#DBEAFE", color: "#2563EB", border: "#BFDBFE" },
};

const STATUS_COLORS = {
  Improving: "#16A34A",
  Declining: "#DC2626",
  Worsening: "#DC2626",
  Stable: COLORS.body,
};

const ScoreCard = ({ label, score, date, color, message }) => (
  <div className="patient-score-card">
    <span className="patient-score-card-label" style={{ color }}>{label}</span>
    <div className="patient-score-card-value" style={{ color }}>
      {score != null ? score : "N/A"}
    </div>
    {date && <span className="patient-score-card-date">{date}</span>}
    {message && <span className="patient-score-card-message">{message}</span>}
  </div>
);

const ChangeCard = ({ label, absoluteChange, percentChangeDisplay, color }) => (
  <div className="patient-score-card">
    <span className="patient-score-card-label" style={{ color }}>{label}</span>
    <div className="patient-score-card-value" style={{ color }}>
      {absoluteChange != null ? absoluteChange : "N/A"}
    </div>
    {percentChangeDisplay && (
      <span className="patient-score-card-date">{percentChangeDisplay}</span>
    )}
  </div>
);

const StatusCard = ({ label, status, color }) => {
  const hasStatus = Boolean(status);
  return (
    <div className="patient-score-card">
      <span className="patient-score-card-label" style={{ color }}>{label}</span>
      <div
        className={`patient-score-card-value${hasStatus ? " patient-status-value" : ""}`}
        style={{ color: hasStatus ? (STATUS_COLORS[status] || COLORS.body) : color }}
      >
        {status || "N/A"}
      </div>
    </div>
  );
};

const AssessmentHistoryTable = ({ title, columns = [], rows = [], color }) => (
  <div className="patient-history-section">
    <h6 className="patient-history-title">{title}</h6>
    <div className="patient-history-table-wrapper">
      <table className="patient-history-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>{row.assessmentDate}</td>
              <td style={{ color: row.isHighRisk ? "#EF4444" : color, fontWeight: 600 }}>
                {row.score}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="patient-history-empty">
                No assessment data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const PatientDetail = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="patient-detail">
        <div className="patient-detail-loading">
          <div className="analytics-loading-spinner" />
          <span className="analytics-loading-text">Loading patient details...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { patient, baselineScores, latestScores, changeScores, statusScores, scoreTrend, assessmentHistory, clinicalTags } = data;

  const phq9Trend = scoreTrend?.yAxis?.find((s) => s.label === "PHQ-9");
  const gad7Trend = scoreTrend?.yAxis?.find((s) => s.label === "GAD-7");

  const genderAge =
    patient?.gender != null && patient?.age != null
      ? `${patient.gender}, ${patient.age}`
      : patient?.gender ?? patient?.age ?? null;

  return (
    <div className="patient-detail">
      <div className="patient-detail-name-card">
        <div className="patient-detail-name-card-row">
          <span className="patient-detail-name">{patient?.full_name || "—"}</span>
          {patient?.dob && (
            <>
              <span className="patient-detail-name-divider" aria-hidden="true" />
              <span className="patient-detail-meta">D.O.B : {patient.dob}</span>
            </>
          )}
          {genderAge && (
            <>
              <span className="patient-detail-name-divider" aria-hidden="true" />
              <span className="patient-detail-meta">{genderAge}</span>
            </>
          )}
        </div>
      </div>

      <div className="patient-detail-cards-grid">
        <div className="patient-detail-card-section">
          <h6 className="patient-detail-section-title">Baseline Scores</h6>
          <div className="patient-detail-card-pair">
            <ScoreCard
              label={baselineScores?.phq9?.label || "PHQ-9"}
              score={baselineScores?.phq9?.score}
              date={baselineScores?.phq9?.date}
              color={COLORS.phq9}
              message={baselineScores?.phq9?.message}
            />
            <ScoreCard
              label={baselineScores?.gad7?.label || "GAD-7"}
              score={baselineScores?.gad7?.score}
              date={baselineScores?.gad7?.date}
              color={COLORS.gad7}
              message={baselineScores?.gad7?.message}
            />
          </div>
        </div>

        <div className="patient-detail-card-section">
          <h6 className="patient-detail-section-title">Latest Scores</h6>
          <div className="patient-detail-card-pair">
            <ScoreCard
              label={latestScores?.phq9?.label || "PHQ-9"}
              score={latestScores?.phq9?.score}
              date={latestScores?.phq9?.date}
              color={COLORS.phq9}
              message={latestScores?.phq9?.message}
            />
            <ScoreCard
              label={latestScores?.gad7?.label || "GAD-7"}
              score={latestScores?.gad7?.score}
              date={latestScores?.gad7?.date}
              color={COLORS.gad7}
              message={latestScores?.gad7?.message}
            />
          </div>
        </div>

        <div className="patient-detail-card-section">
          <h6 className="patient-detail-section-title">Change</h6>
          <div className="patient-detail-card-pair">
            <ChangeCard
              label={changeScores?.phq9?.label || "PHQ-9"}
              absoluteChange={changeScores?.phq9?.absoluteChange}
              percentChangeDisplay={changeScores?.phq9?.percentChangeDisplay}
              color={COLORS.phq9}
            />
            <ChangeCard
              label={changeScores?.gad7?.label || "GAD-7"}
              absoluteChange={changeScores?.gad7?.absoluteChange}
              percentChangeDisplay={changeScores?.gad7?.percentChangeDisplay}
              color={COLORS.gad7}
            />
          </div>
        </div>

        <div className="patient-detail-card-section">
          <h6 className="patient-detail-section-title">Status</h6>
          <div className="patient-detail-card-pair">
            <StatusCard
              label={statusScores?.phq9?.label || "PHQ-9"}
              status={statusScores?.phq9?.status}
              color={COLORS.phq9}
            />
            <StatusCard
              label={statusScores?.gad7?.label || "GAD-7"}
              status={statusScores?.gad7?.status}
              color={COLORS.gad7}
            />
          </div>
        </div>
      </div>

      {scoreTrend && (
        <div className="patient-detail-trends">
          <PatientScoreTrendChart
            title={`Score Trend (PHQ-9)`}
            xAxis={scoreTrend.xAxis || []}
            series={phq9Trend || {}}
            color={COLORS.phq9}
            label="PHQ-9"
          />
          <PatientScoreTrendChart
            title={`Score Trend (GAD-7)`}
            xAxis={scoreTrend.xAxis || []}
            series={gad7Trend || {}}
            color={COLORS.gad7}
            label="GAD-7"
          />
        </div>
      )}

      {assessmentHistory && (
        <div className="patient-detail-history">
          <AssessmentHistoryTable
            title={assessmentHistory.phq9?.title || "PHQ-9 Assessment History"}
            columns={assessmentHistory.phq9?.columns || []}
            rows={assessmentHistory.phq9?.rows || []}
            color={COLORS.phq9}
          />
          <AssessmentHistoryTable
            title={assessmentHistory.gad7?.title || "GAD-7 Assessment History"}
            columns={assessmentHistory.gad7?.columns || []}
            rows={assessmentHistory.gad7?.rows || []}
            color={COLORS.gad7}
          />
        </div>
      )}

      {clinicalTags?.tags?.length > 0 && (
        <div className="patient-detail-tags-section">
          <h6 className="patient-detail-section-title">{clinicalTags.title || "Clinical Tags"}</h6>
          <div className="patient-clinical-tags">
            {clinicalTags.tags.map((tag, i) => {
              const colors = TAG_COLORS[tag.variant] || TAG_COLORS.info;
              return (
                <span
                  key={i}
                  className="patient-clinical-tag"
                  style={{
                    backgroundColor: colors.bg,
                    color: colors.color,
                    borderColor: colors.border,
                  }}
                >
                  {tag.label}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetail;
